/* import __COLOCATED_TEMPLATE__ from './title.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type ContentFragment from 'embercom/models/content-service/content-fragment';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import { isBlank } from '@ember/utils';
import { EntityType } from 'embercom/models/data/entity-types';
import { KNOWLEDGE_HUB_ENTITY_TYPES_TO_DATA_STORES } from 'embercom/lib/knowledge-hub/constants';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import { action } from '@ember/object';
import { type HighlightedResult } from 'embercom/objects/inbox/knowledge-base/search-result';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type Folder from 'embercom/models/content-service/folder';

const MACRO_ROUTE = 'apps.app.settings.helpdesk.macros';
export interface Args {
  contentFragment: ContentFragment;
  isSibling?: boolean;
  hasSibling?: boolean;
  isSiblingContentVisible?: boolean;
  toggleSiblingsVisibility?: () => void;
  locales: string[];
  highlightedTitleArray?: HighlightedResult[];
  // Routes for fodler navigation. Passed down from route templates
  // to keep component decoupled from router. Defaults to Knowledge Hub if not provided.
  parentFolderRoute?: string;
  allContentRoute?: string;
}

type RouteInfo = {
  route: string;
  model1?: string | number;
  model2?: string | number;
  queryParams?: {
    activeContentId?: number | string;
    activeContentType?: string;
    native_content?: boolean;
  };
  linkIdentifier: string;
};

export default class Title extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare intercomEventService: $TSFixMe;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  get title(): string {
    if (this.isFolder && this.contentModel && this.contentModel.title) {
      // If the folder is renamed, we want to use the new title rather than have to refresh the list.
      return this.contentModel.title;
    } else if (this.args.contentFragment.title && !isBlank(this.args.contentFragment.title)) {
      return this.args.contentFragment.title;
    } else {
      return this.intl.t(`knowledge-hub.content-editor.untitled.${objectNames[this.contentType]}`);
    }
  }

  get shouldShowHighlighting(): boolean {
    if (!this.args.highlightedTitleArray || this.isFolder) {
      return false;
    }

    let highlightedText = this.args.highlightedTitleArray.map((block) => block.text).join('');
    return highlightedText === this.title;
  }

  get contentType(): EntityType {
    return this.args.contentFragment.contentType;
  }

  get contentId(): number {
    return this.args.contentFragment.contentId;
  }

  get isFolder() {
    return this.contentType === EntityType.ContentLibraryFolder;
  }

  get isMacro() {
    return this.contentType === EntityType.SavedReply;
  }

  get isDummyArticlesFolder() {
    return (
      this.args.contentFragment.contentId === -1 &&
      this.contentType === EntityType.ContentLibraryFolder
    );
  }

  get contentModel() {
    if (this.isDummyArticlesFolder) {
      return {
        title: this.intl.t('knowledge-hub.folders.native-content-header'),
        get: (prop: string) => {
          if (prop === 'title') {
            return this.intl.t('knowledge-hub.folders.native-content-header');
          }
          if (prop === 'isAiGenerated') {
            return false;
          }
          return undefined;
        },
      };
    }

    return this.store.peekRecord(
      KNOWLEDGE_HUB_ENTITY_TYPES_TO_DATA_STORES[this.contentType],
      this.contentId,
    );
  }

  get isLinkDisabled(): boolean {
    if (!this.isFolder) {
      return false;
    }
    return (this.contentModel as Folder)?.sourceIsBeingDeleted ?? false;
  }

  @action toggleSiblingsVisibility() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'sibling_locale_visibility_stamp',
      section: 'knowledge_hub',
      context: 'all_content',
      place: 'all_content',
    });

    if (this.args.toggleSiblingsVisibility) {
      this.args.toggleSiblingsVisibility();
    }
  }

  get entityType() {
    return this.contentType;
  }

  get entityId() {
    return this.contentId;
  }

  get entityTypeName(): string {
    return objectNames[this.entityType];
  }

  // Returns the route for navigating to folders.
  // Uses the route passed via @parentFolderRoute, or defaults to Knowledge Hub.
  get folderRoute(): string {
    return this.args.parentFolderRoute ?? 'apps.app.knowledge-hub.folder';
  }

  // Returns the route for navigating to all content.
  // Uses the route passed via @allContentRoute, or defaults to Knowledge Hub.
  get articlesContentRoute(): string {
    return this.args.allContentRoute ?? 'apps.app.knowledge-hub.all-content';
  }

  trackClickEvent() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: this.titleLinkIdentifier,
      section: 'knowledge_hub',
    });
  }

  get titleLinkIdentifier() {
    return this.routeInfo.linkIdentifier;
  }

  get routeInfo(): RouteInfo {
    if (this.isDummyArticlesFolder) {
      return {
        route: this.articlesContentRoute,
        queryParams: {
          native_content: true,
        },
        linkIdentifier: 'articles_folder_link',
      };
    }

    switch (this.contentType) {
      case EntityType.ContentLibraryFolder:
        return {
          route: this.folderRoute,
          model1: this.entityId,
          linkIdentifier: 'folder_content_link',
        };
      case EntityType.SavedReply:
        return { route: MACRO_ROUTE, linkIdentifier: 'macro_content_link' };
      default: {
        return {
          route: this.router.currentRouteName,
          queryParams: {
            activeContentId: this.entityId,
            activeContentType: this.entityTypeName,
          },
          linkIdentifier: 'view_content_link',
        };
      }
    }
  }

  get transitionTarget() {
    if (this.isLinkDisabled) {
      return undefined;
    }

    let { route, model1, model2, queryParams } = this.routeInfo;

    if (this.isDummyArticlesFolder) {
      return this.router.urlFor(route, { queryParams });
    }

    if (model1 && model2) {
      return this.router.urlFor(route, model1, model2, { queryParams });
    } else if (model1) {
      return this.router.urlFor(route, model1, { queryParams });
    } else {
      return this.router.urlFor(route, { queryParams });
    }
  }

  @action transitionToContent() {
    if (this.isLinkDisabled) {
      return;
    }

    let { route, model1, model2, queryParams } = this.routeInfo;

    if (this.isDummyArticlesFolder) {
      this.router.transitionTo(route, { queryParams });
      return;
    }

    if (model1 && model2) {
      this.router.transitionTo(route, model1, model2, { queryParams });
    } else if (model1) {
      this.router.transitionTo(route, model1, { queryParams });
    } else if (queryParams && queryParams.activeContentId && queryParams.activeContentType) {
      this.knowledgeHubDrawerEditorService.openViewDrawer({
        activeContentId: queryParams.activeContentId,
        activeContentType: queryParams.activeContentType,
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Table::Cells::Title': typeof Title;
    'knowledge-hub/filterable-list/table/cells/title': typeof Title;
  }
}
